if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.giftMessageVariantId = 47114609229996;
        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('span');

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';
      }

      connectedCallback() {
        // Gift fields sit outside the form, but belong to it via their form attribute.
        this.onGiftFieldChange = (event) => {
          if (event.target.form === this.form) this.syncGiftMessageFields();
        };
        this.onGiftPageShow = () => this.syncGiftMessageFields();
        document.addEventListener('input', this.onGiftFieldChange);
        document.addEventListener('change', this.onGiftFieldChange);
        window.addEventListener('pageshow', this.onGiftPageShow);
        this.syncGiftMessageFields();
      }

      disconnectedCallback() {
        document.removeEventListener('input', this.onGiftFieldChange);
        document.removeEventListener('change', this.onGiftFieldChange);
        window.removeEventListener('pageshow', this.onGiftPageShow);
      }

      syncGiftMessageFields() {
        const controls = Array.from(this.form.elements);
        const yesInput = controls.find((input) => input.matches('[data-bb-gift-toggle="yes"]'));
        const giftBox = yesInput?.closest('[data-bb-gift-message]');
        const isGift = Boolean(yesInput?.checked);
        const quantityInput = controls.find((input) => input.name === 'quantity');
        const quantity = Number.parseInt(quantityInput?.value, 10) || 1;
        const giftId = this.form.querySelector('[data-bb-checkout-gift-id]');
        const giftQuantity = this.form.querySelector('[data-bb-checkout-gift-quantity]');

        if (giftId) giftId.disabled = !isGift;
        if (giftQuantity) {
          giftQuantity.disabled = !isGift;
          giftQuantity.value = String(quantity);
        }
        if (!giftBox) return;

        const fields = giftBox.querySelector('[data-bb-gift-fields]');
        const textarea = giftBox.querySelector('[data-bb-gift-textarea]');
        const from = controls.find((input) => input.name === 'properties[From]');
        const counter = giftBox.querySelector('[data-bb-gift-count]');
        if (fields) fields.hidden = !isGift;
        if (textarea) {
          textarea.required = isGift;
          textarea.disabled = !isGift;
          if (counter) counter.textContent = `${textarea.value.length} / 150`;
        }
        if (from) from.disabled = !isGift;
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        this.syncGiftMessageFields();
        const giftSelection = new FormData(this.form).get('properties[Is this a gift?]');
        if (giftSelection === 'Yes' && !this.form.reportValidity()) return;
        this.handleErrorMessage();

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner').classList.remove('hidden');

        const formData = new FormData(this.form);
        // These fields belong to accelerated checkout only. AJAX builds its own items.
        for (const key of Array.from(formData.keys())) {
          if (key.startsWith('shopify-additional-items[')) formData.delete(key);
        }
        const variantId = formData.get('id');
        const quantity = parseInt(formData.get('quantity')) || 1;
        const isGiftMessageOrder = formData.get('properties[Is this a gift?]') === 'Yes';

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';

        if (isGiftMessageOrder) {
          config.body = JSON.stringify(this.buildGiftMessageCartPayload(formData, variantId, quantity));
        } else {
          delete config.headers['Content-Type'];

          if (this.cart) {
            formData.append(
              'sections',
              this.cart.getSectionsToRender().map((section) => section.id)
            );
            formData.append('sections_url', window.location.pathname);
            this.cart.setActiveElement(document.activeElement);
          }

          config.body = formData;
        }

        const linesUpdateDeferred = this.createCartLinesUpdateEvent(
          isGiftMessageOrder
            ? [
                { merchandiseId: variantId, quantity },
                { merchandiseId: String(this.giftMessageVariantId), quantity },
              ]
            : [{ merchandiseId: variantId, quantity }]
        );

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: variantId,
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);
              this.dispatchCartErrorEvent(response.description || response.message, 'INVALID');
              linesUpdateDeferred?.reject(new Error(response.description || response.message));

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButtonText.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            } else if (!this.cart) {
              this.resolveCartLinesUpdate(linesUpdateDeferred);
              window.location = window.routes.cart_url;
              return;
            }

            this.resolveCartLinesUpdate(linesUpdateDeferred);

            const startMarker = CartPerformance.createStartingMarker('add:wait-for-subscribers');
            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: variantId,
                cartData: response,
              }).then(() => {
                CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker);
              });
            this.error = false;
            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    CartPerformance.measure('add:paint-updated-sections', () => {
                      this.cart.renderContents(response);
                    });
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              CartPerformance.measure('add:paint-updated-sections', () => {
                this.cart.renderContents(response);
              });
            }
          })
          .catch((e) => {
            console.error(e);
            this.handleErrorMessage('Unable to confirm the cart update. Check your cart before trying again.');
            this.dispatchCartErrorEvent(e.message || 'Network error', 'SERVICE_UNAVAILABLE');
            linesUpdateDeferred?.reject(e);
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error) this.submitButton.removeAttribute('aria-disabled');
            this.querySelector('.loading__spinner').classList.add('hidden');

            CartPerformance.measureFromEvent('add:user-action', evt);
          });
      }

      buildGiftMessageCartPayload(formData, variantId, quantity) {
        const payload = {
          items: [
            {
              id: Number(variantId),
              quantity,
              properties: this.getLineItemProperties(formData),
            },
            {
              id: this.giftMessageVariantId,
              quantity,
            },
          ],
        };

        if (this.cart) {
          payload.sections = this.cart.getSectionsToRender().map((section) => section.id);
          payload.sections_url = window.location.pathname;
          this.cart.setActiveElement(document.activeElement);
        }

        return payload;
      }

      getLineItemProperties(formData) {
        const properties = {};

        for (const [key, value] of formData.entries()) {
          const match = key.match(/^properties\[(.+)\]$/);

          if (match && value) {
            properties[match[1]] = value;
          }
        }

        return properties;
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          this.submitButtonText.textContent = window.variantStrings.addToCart;
        }
      }

      createCartLinesUpdateEvent(lines) {
        const { CartLinesUpdateEvent } = window.StandardEvents || {};
        if (!CartLinesUpdateEvent) return null;

        const deferred = CartLinesUpdateEvent.createPromise();
        this.dispatchEvent(
          new CartLinesUpdateEvent({
            action: 'add',
            context: 'product',
            lines,
            promise: deferred.promise,
          })
        );
        return deferred;
      }

      resolveCartLinesUpdate(deferred) {
        if (!deferred) return;
        const { CartLinesUpdateEvent } = window.StandardEvents || {};
        if (!CartLinesUpdateEvent) return;

        const pendingCartDataPromise =
          typeof CartItems !== 'undefined'
            ? CartItems.fetchCartData()
            : fetch(`${routes.cart_url}.json`).then((response) => response.json());

        pendingCartDataPromise
          .then((cart) => {
            if (!cart?.currency) return deferred.reject(new Error('Missing currency in cart response'));
            deferred.resolve({ cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart) });
          })
          .catch((e) => deferred.reject(e));
      }

      dispatchCartErrorEvent(message, code) {
        const { CartErrorEvent } = window.StandardEvents || {};
        if (!CartErrorEvent) return;
        this.dispatchEvent(new CartErrorEvent({ error: message, code }));
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    }
  );
}